// frontend/src/telephony/activeCallRecording/useActiveCallRecording.ts
//
// State machine for the mid-call Record Call toggle.
//
// Lives entirely inside the ActiveCall UI — mounted when a call is
// active, unmounted when it ends. Reflects the REAL backend recording
// state (via realtime events + start/stop mutation responses) rather
// than optimistic-only local state. If a start/stop fails, the toggle
// stays on its last confirmed value and surfaces an error string.
//
// Workspace policy 'always' auto-invokes start EXACTLY ONCE per
// transition to a truly-connected call ('in_call') so the caller
// cannot accidentally begin an unrecorded conversation. If the auto-
// start fails, the toggle stays OFF and an actionable error is
// exposed to the caller — never falsely showing ON.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useStartCallRecordingMutation,
  useStopCallRecordingMutation,
} from '../../api/telephonyApi';
import { realtimeClient } from '../../realtime/socketClient';
import { RealtimeEventType } from '../../realtime/events';
import { useAuth } from '../../hooks/useAuth';

export type ActiveRecordingStatus =
  | 'idle'
  | 'starting'
  | 'recording'
  | 'stopping'
  | 'error';

export interface UseActiveCallRecordingArgs {
  /** Twilio Call SID of the ACTIVE call (null when no active call). */
  callSid: string | null;
  /** 'off' | 'always' | undefined (unknown). */
  workspaceRecordingMode: 'off' | 'always' | undefined;
  /** `voice.activeCall.kind` from useTwilioVoiceDevice. */
  activeCallKind: string;
  /**
   * When true, auto-fire `start()` exactly once as the call reaches
   * `in_call`. Used by the caller to honor the user's Recording
   * preference (Automatic) — same guard as the workspace-always path
   * so we never double-start.
   */
  autoStartOnConnect?: boolean;
  /**
   * Phase 1 linking — the CRM's own outboundCallId for the currently
   * active Outbound-managed call. Threaded from the prepare response
   * through the ActiveCall state, this ID lets the backend link the
   * new CallRecording.callSessionId to the placeholder CallSession
   * (whose `twilioCallSid` is still null in this window). Without
   * this the Recordings tab renders "Unknown caller" + em-dashes
   * for direction / duration / agent / phone number.
   *
   * Optional; native CRM calls omit it and behave byte-identically.
   */
  outboundCallId?: string | null;
}

export interface UseActiveCallRecordingResult {
  status: ActiveRecordingStatus;
  /** The CallRecording row id when status is 'recording' / 'stopping'. */
  recordingId: string | null;
  /** Actionable error string when status === 'error'. */
  error: string | null;
  /** true when workspace policy locks recording ON. */
  workspaceForced: boolean;
  /** Whether the control is visible at all (mid-call surfaces only). */
  visible: boolean;
  /** Whether the toggle should render disabled (during pending/forced). */
  disabled: boolean;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  /** Clear a transient error banner. */
  dismissError: () => void;
}

/**
 * Realtime event payloads we consume. Loose shapes — the socket layer
 * hands us `unknown`.
 */
type RtStartedPayload = {
  workspaceId?: string;
  twilioCallSid?: string;
  recordingId?: string;
};
type RtStoppedPayload = RtStartedPayload;

export function useActiveCallRecording(
  args: UseActiveCallRecordingArgs,
): UseActiveCallRecordingResult {
  const {
    callSid,
    workspaceRecordingMode,
    activeCallKind,
    autoStartOnConnect,
    outboundCallId,
  } = args;

  const auth = useAuth();
  const workspaceId = auth.user?.activeWorkspaceId ?? null;

  const [status, setStatus] = useState<ActiveRecordingStatus>('idle');
  const [recordingId, setRecordingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const workspaceForced = workspaceRecordingMode === 'always';
  const inActiveState =
    activeCallKind === 'connecting' || activeCallKind === 'in_call';
  const visible = inActiveState;
  const disabled =
    status === 'starting' ||
    status === 'stopping' ||
    workspaceForced ||
    !callSid;

  const [startMut] = useStartCallRecordingMutation();
  const [stopMut] = useStopCallRecordingMutation();

  // Reset state when the call ends. Doing this here (not on launch
  // resolution) is what makes the toggle keep its state during the
  // active call — see useOutgoingCallLauncher for the same pattern.
  const prevKindRef = useRef<string>(activeCallKind);
  useEffect(() => {
    const prev = prevKindRef.current;
    const curr = activeCallKind;
    prevKindRef.current = curr;
    if (prev === curr) return;
    const terminal =
      curr === 'idle' || curr === 'ended' || curr === 'failed';
    if (terminal) {
      setStatus('idle');
      setRecordingId(null);
      setError(null);
      autoStartAttemptedRef.current = false;
    }
  }, [activeCallKind]);

  // start() / stop() API wrappers ─────────────────────────────────
  const start = useCallback(async () => {
    if (!callSid) return;
    if (status === 'starting' || status === 'recording' || status === 'stopping') {
      return;
    }
    setStatus('starting');
    setError(null);
    try {
      const res = await startMut({
        callSid,
        // Optional linking hint — backend uses it to bind the new
        // CallRecording to the Outbound-managed placeholder CallSession
        // (see the linking comment on UseActiveCallRecordingArgs).
        ...(typeof outboundCallId === 'string' && outboundCallId.length > 0
          ? { outboundCallId }
          : {}),
      }).unwrap();
      // Two 200 shapes: {recording:{...}} on normal success/idempotency
      // hit, and {ownedBy:'twiml'} (no recording) when the initial TwiML
      // is the one that started the recording (provider-owned automatic
      // + outbound). Recording IS active in both cases — the mid-call
      // stop button just can't target a specific segment id in the TwiML
      // case (backend has no CRM segment row for it).
      if (res.recording) {
        setRecordingId(res.recording.id);
      } else {
        setRecordingId(null);
      }
      setStatus('recording');
    } catch (err: unknown) {
      const msg =
        (err as { data?: { message?: string; error?: string } })?.data
          ?.message ??
        (err as { data?: { error?: string } })?.data?.error ??
        (err instanceof Error ? err.message : 'Failed to start recording');
      setError(msg);
      // Never falsely show ON.
      setStatus('error');
    }
  }, [callSid, status, startMut, outboundCallId]);

  const stop = useCallback(async () => {
    if (!recordingId) return;
    if (status !== 'recording') return;
    // Compliance lock: workspace-always cannot be stopped.
    if (workspaceForced) return;
    setStatus('stopping');
    setError(null);
    try {
      await stopMut({ recordingId }).unwrap();
      // Server has flipped row to 'stopped'; we clear local record
      // state so a subsequent start creates a new segment.
      setRecordingId(null);
      setStatus('idle');
    } catch (err: unknown) {
      const msg =
        (err as { data?: { message?: string; error?: string } })?.data
          ?.message ??
        (err as { data?: { error?: string } })?.data?.error ??
        (err instanceof Error ? err.message : 'Failed to stop recording');
      setError(msg);
      // If stop failed, we're back to 'recording' (server-side state
      // is still active). User can retry.
      setStatus('recording');
    }
  }, [recordingId, status, stopMut, workspaceForced]);

  // Auto-start — fires EXACTLY once per connected transition. Fires
  // when EITHER (a) workspace policy locks recording ON, OR (b) the
  // caller has opted in via `autoStartOnConnect` (Recording Mode
  // preference resolves to Automatic). Ref guard survives re-renders
  // inside the same call so we never double-start.
  const autoStartAttemptedRef = useRef<boolean>(false);
  useEffect(() => {
    if (!(workspaceForced || autoStartOnConnect)) return;
    if (activeCallKind !== 'in_call') return;
    if (!callSid) return;
    if (autoStartAttemptedRef.current) return;
    autoStartAttemptedRef.current = true;
    // Fire-and-forget; start() manages its own status/error state.
    void start();
  }, [workspaceForced, autoStartOnConnect, activeCallKind, callSid, start]);

  // Subscribe to realtime lifecycle events. We only trust events that
  // match our current callSid AND workspace — the socket delivers
  // workspace-scoped topics so this is defense-in-depth.
  useEffect(() => {
    if (!workspaceId || !callSid) return;
    const topic = `workspace:${workspaceId}`;

    const unsubStarted = realtimeClient.on(
      topic,
      (payload: unknown) => {
        const p = (payload ?? {}) as RtStartedPayload;
        if (p.workspaceId !== workspaceId) return;
        if (p.twilioCallSid !== callSid) return;
        // Only trust a STARTED event if we don't already have a local
        // recording id — otherwise it might be our own emit rebroadcast
        // or a stale delivery from a prior segment.
        if (!recordingId && typeof p.recordingId === 'string') {
          setRecordingId(p.recordingId);
          setStatus('recording');
        }
      },
      RealtimeEventType.CALL_RECORDING_STARTED,
    );

    const unsubStopped = realtimeClient.on(
      topic,
      (payload: unknown) => {
        const p = (payload ?? {}) as RtStoppedPayload;
        if (p.workspaceId !== workspaceId) return;
        if (p.twilioCallSid !== callSid) return;
        // Trust: server said this recording stopped. Flip UI back to
        // idle regardless of who initiated the stop (could be another
        // tab of the same user).
        if (!p.recordingId || p.recordingId === recordingId) {
          setRecordingId(null);
          setStatus('idle');
        }
      },
      RealtimeEventType.CALL_RECORDING_STOPPED,
    );

    return () => {
      unsubStarted?.();
      unsubStopped?.();
    };
  }, [workspaceId, callSid, recordingId]);

  const dismissError = useCallback(() => {
    setError(null);
    // If we were parked on 'error', drop back to idle so the user can
    // retry cleanly.
    if (status === 'error') setStatus('idle');
  }, [status]);

  return useMemo(
    () => ({
      status,
      recordingId,
      error,
      workspaceForced,
      visible,
      disabled,
      start,
      stop,
      dismissError,
    }),
    [
      status,
      recordingId,
      error,
      workspaceForced,
      visible,
      disabled,
      start,
      stop,
      dismissError,
    ],
  );
}

export default useActiveCallRecording;
